<template>
  <section class="pbv-scope">
    <header class="pbv-scope__head">
      <div class="pbv-scope__title">
        <span class="pbv-scope__title-icon">
          <v-icon size="16" color="#FFFFFF">{{ icons.mdiVideoOutline }}</v-icon>
        </span>
        Vidéo à transcrire
      </div>
      <div v-if="platformLabel" class="pbv-scope__meta">
        Plateforme détectée : {{ platformLabel }}
      </div>
    </header>

    <div class="pbv-scope__panel">
      <div class="pbv-scope__grid">
        <div class="pbv-scope__field pbv-scope__field--legacy pbv-scope__site">
          <label class="rmr-table-shell__filter-label">Site</label>
          <site-filter
            site-list-getter="aiFactory/getSiteConnectors"
            item-value="site.id"
            item-text="site.name"
            store="aiFactory"
            store-getter="getSelectedSites"
            :multiple="false"
            :depends-on-site-groups="false"
            dense
          />
        </div>

        <div class="pbv-scope__field pbv-scope__url">
          <label class="rmr-table-shell__filter-label">URL de la vidéo</label>
          <rmr-text-field
            :value="video.state.url"
            placeholder="https://www.youtube.com/watch?v=…  ·  instagram.com/reel/…  ·  tiktok.com/…"
            :prepend-inner-icon="icons.mdiLinkVariant"
            hide-details
            clearable
            @input="video.state.url = $event || ''"
            @keyup.enter="$emit('analyze')"
          />
        </div>

        <rmr-action-button
          class="pbv-scope__go"
          label="Analyser"
          :icon="icons.mdiMagnify"
          variant="primary"
          size="md"
          :loading="video.state.analyzing"
          :disabled="!video.canAnalyze.value"
          @click="$emit('analyze')"
        />
      </div>

      <!--
        Le choix de la brique et le contournement du cache sont des réglages
        de mise au point, pas d'usage courant : présents, mais repliés au
        premier abord (demande du 25/09/2026, ouverture de la page au pôle).
        Un réglage actif rouvre la section, pour qu'il ne s'applique jamais
        à l'insu de l'éditeur.
      -->
      <button
        type="button"
        class="pbv-scope__advanced-toggle"
        :aria-expanded="advancedOpen ? 'true' : 'false'"
        @click="toggleAdvanced"
      >
        <v-icon size="16" color="currentColor">
          {{ advancedOpen ? icons.mdiChevronUp : icons.mdiChevronDown }}
        </v-icon>
        Options avancées
        <span
          v-if="!advancedOpen && hasOverride"
          class="pbv-scope__advanced-dot"
        />
      </button>

      <v-expand-transition>
        <div v-show="advancedOpen" class="pbv-scope__advanced">
          <div class="pbv-scope__field pbv-scope__provider">
            <label class="rmr-table-shell__filter-label">
              Brique de transcription
              <rmr-soft-badge
                v-if="video.selectedProvider.value"
                :label="
                  video.selectedProvider.value.has_timestamps
                    ? 'time codes'
                    : 'sans time codes'
                "
                :color="
                  video.selectedProvider.value.has_timestamps
                    ? tokens.RMR_SUCCESS
                    : tokens.RMR_WARNING
                "
                size="xs"
                class="ml-2"
              />
            </label>
            <rmr-select
              :value="video.state.provider"
              :items="video.providerItems.value"
              item-text="text"
              item-value="value"
              no-chips
              size="sm"
              @input="video.state.provider = $event || ''"
            />
          </div>

          <div class="pbv-scope__field pbv-scope__force">
            <rmr-toggle-card
              :value="video.state.force"
              label="Ignorer le cache"
              size="sm"
              @input="video.state.force = $event"
            />
          </div>
        </div>
      </v-expand-transition>
    </div>

    <!--
      Le choix est STRICT côté serveur : pas de fallback sur une autre brique.
      On prévient donc AVANT l'analyse de ce que la brique choisie ne sait pas
      faire, plutôt que de le découvrir sur l'interrupteur grisé.
    -->
    <rmr-notice
      v-if="video.providerLacksTimestamps.value"
      variant="warning"
      class="pbv-scope__notice"
      text="Cette brique ne rend pas de time codes : la découpe en plusieurs angles sera désactivée pour cette vidéo."
    />
  </section>
</template>

<script>
import { computed, ref, watch } from "@vue/composition-api";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiLinkVariant,
  mdiMagnify,
  mdiVideoOutline,
} from "@mdi/js";

import SiteFilter from "@/components/common/filters/SiteFilter";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrToggleCard from "@/components/ui/forms/RmrToggleCard.vue";
import * as tokens from "@/styles/tokens";

// Préférence d'affichage par navigateur, même convention de clé que
// `audience/perf-per-article/article-list`. Lecture et écriture protégées :
// navigation privée ou stockage bloqué ne doivent pas casser la page.
const ADVANCED_STORAGE_KEY = "ai-factory/post-by-video/advanced-open";

const readAdvancedOpen = () => {
  try {
    return localStorage.getItem(ADVANCED_STORAGE_KEY) === "1";
  } catch (error) {
    return false;
  }
};

const writeAdvancedOpen = (open) => {
  try {
    localStorage.setItem(ADVANCED_STORAGE_KEY, open ? "1" : "0");
  } catch (error) {
    // Rien : la préférence ne survivra pas au rechargement, c'est tout.
  }
};

const PLATFORM_LABELS = {
  youtube: "YouTube",
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X (Twitter)",
  other: "Autre (voie audio)",
};

export default {
  name: "VideoScopeBanner",
  components: {
    RmrActionButton,
    RmrNotice,
    RmrSelect,
    RmrSoftBadge,
    RmrTextField,
    RmrToggleCard,
    SiteFilter,
  },
  props: {
    /** Le hook `useVideoTranscription` de la page (état + actions). */
    video: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    // =>  DATA
    const advancedOpen = ref(readAdvancedOpen());

    // =>  COMPUTED
    const platformLabel = computed(
      () => PLATFORM_LABELS[props.video.urlPlatform.value] || ""
    );

    // Une brique choisie explicitement ou le cache ignoré : un réglage qui
    // change le résultat de l'analyse.
    const hasOverride = computed(
      () =>
        Boolean(props.video.state.provider) || Boolean(props.video.state.force)
    );

    // =>  METHODS
    const toggleAdvanced = () => {
      advancedOpen.value = !advancedOpen.value;
      writeAdvancedOpen(advancedOpen.value);
    };

    // =>  WATCH
    // Un réglage actif ne reste jamais caché : on déplie sans toucher à la
    // préférence, pour que la section se referme d'elle-même la fois suivante.
    watch(
      hasOverride,
      (active) => {
        if (active) advancedOpen.value = true;
      },
      { immediate: true }
    );

    return {
      advancedOpen,
      hasOverride,
      icons: {
        mdiChevronDown,
        mdiChevronUp,
        mdiLinkVariant,
        mdiMagnify,
        mdiVideoOutline,
      },
      platformLabel,
      toggleAdvanced,
      tokens,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/rmr-legacy-field";

.pbv-scope {
  background: linear-gradient(
    135deg,
    $rmr-indigo-dark 0%,
    $rmr-indigo-accent 45%,
    $rmr-indigo 100%
  );
  border-radius: $rmr-radius-3xl;
  padding: 18px 20px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}

.pbv-scope__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 4px 14px;
}

.pbv-scope__title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $rmr-surface-0;
  font-size: 15px;
  font-weight: 700;
}

.pbv-scope__title-icon {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: $rmr-radius-sm;
  background: rgba(255, 255, 255, 0.2);
}

.pbv-scope__meta {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.pbv-scope__panel {
  padding: 18px;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-0;
}

.pbv-scope__grid,
.pbv-scope__advanced {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px;
  align-items: end;
}

.pbv-scope__advanced {
  padding-top: 14px;
}

// Discret par construction : un texte de la taille des libellés, sans boîte,
// juste un chevron. Il ne doit pas concurrencer le bouton « Analyser ».
.pbv-scope__advanced-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding: 2px 4px 2px 0;
  border: 0;
  background: none;
  color: $rmr-text-muted;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: $rmr-indigo;
    outline: none;
  }
}

.pbv-scope__advanced-dot {
  width: 6px;
  height: 6px;
  margin-left: 2px;
  border-radius: 50%;
  background: $rmr-indigo;
}

.pbv-scope__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.pbv-scope__field--legacy {
  @include gw-legacy-field;
  @include gw-legacy-field-label;
}

.pbv-scope__site {
  grid-column: span 3;
}

.pbv-scope__url {
  grid-column: span 6;
}

.pbv-scope__provider {
  grid-column: span 5;
}

// Le toggle n'a pas de boîte à lui : centré dans la hauteur commune pour que
// sa cellule s'aligne en bas comme les autres.
.pbv-scope__force {
  grid-column: span 3;
  justify-content: center;
  min-height: $gw-control-height;
}

.pbv-scope__go {
  grid-column: span 3;
  width: 100%;
}

.pbv-scope__notice {
  margin-top: 12px;
}

@media (max-width: 1264px) {
  .pbv-scope__grid,
  .pbv-scope__advanced {
    grid-template-columns: 1fr 1fr;
  }

  .pbv-scope__site,
  .pbv-scope__url,
  .pbv-scope__provider,
  .pbv-scope__force,
  .pbv-scope__go {
    grid-column: span 1;
  }

  .pbv-scope__url {
    grid-column: span 2;
  }
}

@media (max-width: 700px) {
  .pbv-scope__grid,
  .pbv-scope__advanced {
    grid-template-columns: 1fr;
  }

  .pbv-scope__url {
    grid-column: span 1;
  }
}
</style>
